/**
 * 前端监控入口
 *
 * 唯一对外导出 setupMonitor()。总开关关闭时直接返回，不注册任何监听器、
 * 不起定时器，零运行时开销。
 */
import type { App } from 'vue';
import type { Router } from 'vue-router';
import { setupJsErrorCollector } from '@/monitor/collectors/jsError';
import { setupPerformanceCollector } from '@/monitor/collectors/performance';
import { setupResourceCollector } from '@/monitor/collectors/resource';
import { setupVueErrorCollector } from '@/monitor/collectors/vueError';
import { MONITOR_CONFIG } from '@/monitor/config';
import { sweep, track } from '@/monitor/core/aggregator';
import { setRouteReader } from '@/monitor/core/context';
import { configureReporter, flush, flushBeacon, setDryRun } from '@/monitor/core/reporter';

declare global {
  interface Window {
    /** 开发环境调试钩子，生产构建不挂载 */
    __monitor?: {
      /** 发送各类型自检告警 */
      test: () => void;
      /** 立即结算并发送队列 */
      flush: () => void;
      /** 开关 dry-run：开启后只打 console 不发网络请求 */
      dryRun: (on?: boolean) => void;
    };
  }
}

/** 构造覆盖各采集类型的自检告警，用于验证链路是否通 */
const sendTestEvents = (): void => {
  const now = Date.now();
  track({
    type: 'js_error',
    level: 'error',
    title: '【自检】JS 运行时异常',
    message: 'monitor self test',
    source: 'devtools:1:1',
    stack: '    at __monitor.test (devtools:1:1)',
    timestamp: now,
  });
  track({
    type: 'api_error',
    level: 'error',
    title: '【自检】接口异常',
    message: 'HTTP 500',
    source: 'GET /api/self-test → 500',
    extra: { 耗时: '120ms' },
    timestamp: now,
  });
  track({
    type: 'performance',
    level: 'warn',
    title: '【自检】性能指标超标',
    message: 'LCP 超过阈值',
    source: 'LCP',
    extra: { 实测: '5200ms', 阈值: '4000ms' },
    timestamp: now,
  });
  sweep();
  flush();
};

export const setupMonitor = (app: App, router: Router): void => {
  /**
   * 有意例外（CLAUDE.md「非必要不加 try/catch」）：
   * 监控初始化失败绝不能阻断主应用启动，必须在此兜住。
   */
  try {
    if (import.meta.env.VITE_MONITOR_ENABLED !== 'true') return;
    /**
     * 本地开发服务器默认不上报：dev 模式首次进入懒加载路由要现场编译 chunk，
     * 路由切换耗时天然超标，本地点几下页面就会往告警群发一堆伪告警。
     * 需要在本地调试监控时，在 .env.development.local 里加 VITE_MONITOR_LOCAL=true。
     */
    if (__IS_DEV_SERVER__ && import.meta.env.VITE_MONITOR_LOCAL !== 'true') return;

    configureReporter({ webhook: import.meta.env.VITE_MONITOR_WEBHOOK || '/dingtalk-webhook' });
    setRouteReader(() => router.currentRoute.value.fullPath);

    setupJsErrorCollector();
    setupVueErrorCollector(app);
    setupResourceCollector();
    setupPerformanceCollector(router);

    /** 微批：定时结算冷却窗口并发送队列 */
    window.setInterval(() => {
      sweep();
      flush();
    }, MONITOR_CONFIG.FLUSH_INTERVAL);

    /** 页面卸载前把队列残留发出去 */
    window.addEventListener('pagehide', () => {
      sweep();
      flushBeacon();
    });

    if (!import.meta.env.PROD) {
      window.__monitor = {
        test: sendTestEvents,
        flush: () => {
          sweep();
          flush();
        },
        dryRun: (on = true) => setDryRun(on),
      };
    }
  } catch {
    /** 监控初始化失败时静默降级，不影响主应用 */
  }
};
